import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Header from '../components/common/Header';
import PageHeader from '../components/common/PageHeader';
import Snackbar from '../components/common/Snackbar';
import useSnackbar from '../hooks/useSnackbar';
import {
  searchCustomerNicknames,
  sendMarketingPush,
} from '../services/marketingNotificationService';
import '../css/MarketingNotification.css';

const TITLE_MAX = 60;
const BODY_MAX = 200;
const MAX_TARGETS = 500;

const MarketingNotification = () => {
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

  const [nicknameInput, setNicknameInput] = useState('');
  const [selected, setSelected] = useState([]); // [{ nickname, phone? }]
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  const canSubmit = useMemo(
    () => selected.length > 0 && title.trim().length > 0 && body.trim().length > 0 && !sending,
    [selected.length, title, body, sending]
  );

  const selectedNicknameSet = useMemo(
    () => new Set(selected.map((s) => s.nickname)),
    [selected]
  );

  // debounced 자동완성
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = nicknameInput.trim();
    if (q.length < 1) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current = false;
      abortRef.current = true;
      try {
        const res = await searchCustomerNicknames(q, 10);
        if (!abortRef.current) return;
        const items = (res?.data ?? []).filter(
          (c) => !selectedNicknameSet.has(c.nickname)
        );
        setSuggestions(items);
        setShowDropdown(items.length > 0);
      } catch (e) {
        if (abortRef.current) {
          setSuggestions([]);
          setShowDropdown(false);
        }
      }
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [nicknameInput, selectedNicknameSet]);

  const addTarget = useCallback(
    (item) => {
      if (!item?.nickname) return;
      if (selectedNicknameSet.has(item.nickname)) return;
      if (selected.length >= MAX_TARGETS) {
        showSnackbar(`최대 ${MAX_TARGETS}명까지만 지정 가능합니다.`);
        return;
      }
      setSelected((prev) => [
        ...prev,
        {
          nickname: item.nickname,
          phone: item.phone,
          // 검색으로 얻은 항목이면 boolean 그대로, 직접 입력이면 undefined
          marketingConsented: item.marketingConsented,
        },
      ]);
      if (item.marketingConsented === false) {
        showSnackbar(`${item.nickname} 은 마케팅 미동의라 발송에서 제외됩니다`, 'warning');
      }
      setNicknameInput('');
      setSuggestions([]);
      setShowDropdown(false);
    },
    [selected.length, selectedNicknameSet, showSnackbar]
  );

  const removeTarget = useCallback((nickname) => {
    setSelected((prev) => prev.filter((s) => s.nickname !== nickname));
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const q = nicknameInput.trim();
      if (!q) return;
      // 자동완성 첫 항목이 있으면 그걸, 없으면 입력값 그대로 (서버에서 매칭 실패로 판별됨)
      const first = suggestions[0];
      if (first) {
        addTarget(first);
      } else {
        addTarget({ nickname: q });
      }
    } else if (e.key === 'Backspace' && !nicknameInput && selected.length > 0) {
      removeTarget(selected[selected.length - 1].nickname);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const nonConsentCount = selected.filter((s) => s.marketingConsented === false).length;
    const willSendCount = selected.length - nonConsentCount;
    const consentNote = nonConsentCount > 0
      ? `\n\n※ 이 중 ${nonConsentCount}명은 마케팅 미동의라 자동 제외됩니다.\n실제 발송 예상: ${willSendCount}명`
      : '';
    if (!window.confirm(
      `${selected.length}명에게 마케팅 푸시를 발송합니다.\n\n제목: ${title.trim()}\n본문: ${body.trim()}${consentNote}\n\n계속하시겠습니까?`
    )) {
      return;
    }
    setSending(true);
    setLastResult(null);
    try {
      const res = await sendMarketingPush({
        targetNicknames: selected.map((s) => s.nickname),
        title: title.trim(),
        body: body.trim(),
      });
      setLastResult(res?.data ?? null);
      showSnackbar(
        `발송 완료: ${res?.data?.sent ?? 0}/${res?.data?.requested ?? selected.length}명 성공`,
        'success'
      );
    } catch (e) {
      showSnackbar(e?.message || '발송에 실패했습니다.');
    } finally {
      setSending(false);
    }
  };

  const handleReset = () => {
    setSelected([]);
    setTitle('');
    setBody('');
    setNicknameInput('');
    setLastResult(null);
  };

  return (
    <>
      <Header />
      <div className="marketing-notification-page">
        <PageHeader title="마케팅 푸시 발송" />

        <div className="marketing-form">
          <section className="form-section">
            <label className="form-label">
              대상 닉네임
              <span className="form-hint">
                {selected.length}/{MAX_TARGETS}
              </span>
            </label>
            <div className="target-input-wrapper">
              <div className="target-tags">
                {selected.map((s) => (
                  <span
                    key={s.nickname}
                    className={`target-tag${s.marketingConsented === false ? ' target-tag-no-consent' : ''}`}
                    title={s.marketingConsented === false ? '마케팅 미동의 — 발송에서 자동 제외됨' : undefined}
                  >
                    {s.nickname}
                    {s.marketingConsented === false && (
                      <span className="target-tag-badge">미동의</span>
                    )}
                    <button
                      type="button"
                      className="target-tag-remove"
                      onClick={() => removeTarget(s.nickname)}
                      aria-label={`${s.nickname} 제외`}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  className="target-input"
                  value={nicknameInput}
                  onChange={(e) => setNicknameInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={() => nicknameInput && setShowDropdown(suggestions.length > 0)}
                  onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                  placeholder={selected.length === 0 ? '닉네임 입력 (Enter 로 추가)' : ''}
                />
              </div>
              {showDropdown && suggestions.length > 0 && (
                <ul className="suggestion-dropdown">
                  {suggestions.map((c) => (
                    <li
                      key={c.customerId}
                      className={`suggestion-item${c.marketingConsented === false ? ' suggestion-item-no-consent' : ''}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        addTarget(c);
                      }}
                      title={c.marketingConsented === false ? '마케팅 미동의 — 선택해도 발송 대상에서 제외됨' : undefined}
                    >
                      <span className="suggestion-nickname">{c.nickname}</span>
                      <span className="suggestion-meta">
                        {c.marketingConsented === false ? (
                          <span className="suggestion-badge suggestion-badge-warn">미동의</span>
                        ) : (
                          <span className="suggestion-badge suggestion-badge-ok">동의</span>
                        )}
                        {c.phone && <span className="suggestion-phone">{c.phone}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          <section className="form-section">
            <label className="form-label">
              제목
              <span className="form-hint">
                {title.length}/{TITLE_MAX}
              </span>
            </label>
            <input
              type="text"
              className="text-input"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))}
              placeholder="예: 주변에 새 카공 카페가 열렸어요"
              maxLength={TITLE_MAX}
            />
          </section>

          <section className="form-section">
            <label className="form-label">
              본문
              <span className="form-hint">
                {body.length}/{BODY_MAX}
              </span>
            </label>
            <textarea
              className="textarea-input"
              rows={4}
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, BODY_MAX))}
              placeholder="예: 3분 거리에 콘센트 넉넉한 카페 오픈! 지금 확인해보세요."
              maxLength={BODY_MAX}
            />
          </section>

          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleReset}
              disabled={sending}
            >
              초기화
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {sending ? '발송 중…' : `${selected.length}명에게 발송`}
            </button>
          </div>

          {lastResult && (
            <section className="result-section">
              <h3 className="result-title">발송 결과</h3>
              <div className="result-summary">
                <div className="result-stat result-stat-primary">
                  <span className="stat-label">성공</span>
                  <span className="stat-value">{lastResult.sent}</span>
                </div>
                <div className="result-stat">
                  <span className="stat-label">요청</span>
                  <span className="stat-value">{lastResult.requested}</span>
                </div>
                <div className="result-stat">
                  <span className="stat-label">미매칭</span>
                  <span className="stat-value">{lastResult.skippedNotFound}</span>
                </div>
                <div className="result-stat">
                  <span className="stat-label">미동의</span>
                  <span className="stat-value">{lastResult.skippedNoConsent}</span>
                </div>
                <div className="result-stat">
                  <span className="stat-label">FCM 미등록</span>
                  <span className="stat-value">{lastResult.skippedNoFcmToken}</span>
                </div>
                <div className="result-stat">
                  <span className="stat-label">실패</span>
                  <span className="stat-value">{lastResult.failed}</span>
                </div>
              </div>

              {lastResult.notFoundNicknames?.length > 0 && (
                <details className="result-detail">
                  <summary>미매칭 닉네임 ({lastResult.notFoundNicknames.length})</summary>
                  <div className="detail-list">{lastResult.notFoundNicknames.join(', ')}</div>
                </details>
              )}
              {lastResult.noConsentNicknames?.length > 0 && (
                <details className="result-detail">
                  <summary>마케팅 미동의 ({lastResult.noConsentNicknames.length})</summary>
                  <div className="detail-list">{lastResult.noConsentNicknames.join(', ')}</div>
                </details>
              )}
              {lastResult.noFcmTokenNicknames?.length > 0 && (
                <details className="result-detail">
                  <summary>FCM 토큰 미등록 ({lastResult.noFcmTokenNicknames.length})</summary>
                  <div className="detail-list">{lastResult.noFcmTokenNicknames.join(', ')}</div>
                </details>
              )}
              {lastResult.failedNicknames?.length > 0 && (
                <details className="result-detail">
                  <summary>발송 실패 ({lastResult.failedNicknames.length})</summary>
                  <div className="detail-list">{lastResult.failedNicknames.join(', ')}</div>
                </details>
              )}
            </section>
          )}
        </div>
      </div>
      <Snackbar
        isOpen={snackbar.isOpen}
        message={snackbar.message}
        type={snackbar.type}
        onClose={hideSnackbar}
      />
    </>
  );
};

export default MarketingNotification;
